import { Icon } from "@/components/ui/icon";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { cn } from "@/lib/utils";
import { Link, usePathname } from "expo-router";
import { useTranslation } from "react-i18next";
import { Pressable, View } from "react-native";

import {
    APP_NAV_ITEMS,
    isAppNavItemActive,
    type AppNavItem,
} from "./appNavItems";

import BasisPragueLogo from "@/assets/images/basis_prague_logo.svg";
import { Button } from "@/components/ui/button";
import { ParentUserCard } from "@/features/menu/ParentUserCard";
import { useAuth } from "../../lib/auth";
import { TEST_IDS } from "../../testIdDefinition";

type SidebarNavItemProps = {
    item: AppNavItem;
    isActive: boolean;
};

function SidebarNavItem({ item, isActive }: SidebarNavItemProps) {
    const { t } = useTranslation();

    return (
        <Link href={item.href} asChild>
            <Pressable
                testID={item.testId}
                className={cn(
                    "flex-row items-center gap-3 rounded-md px-3 py-2.5 active:bg-accent",
                    isActive ? "bg-primary-soft" : "bg-transparent",
                )}
                accessibilityRole="link"
                accessibilityState={{ selected: isActive }}
            >
                <View className="w-5 items-center justify-center">
                    <Icon
                        name={item.icon}
                        iconStyle={isActive ? "solid" : "regular"}
                        size={16}
                        className={
                            isActive ? "text-primary" : "text-muted-foreground"
                        }
                    />
                </View>
                <Text
                    variant="label"
                    className={isActive ? "text-primary" : undefined}
                >
                    {t(`nav.${item.labelKey}`)}
                </Text>
            </Pressable>
        </Link>
    );
}

export function AppSidebar() {
    const pathname = usePathname();
    const { signOut } = useAuth();
    const { t } = useTranslation();

    return (
        <View className="h-full w-60 shrink-0 border-r border-border bg-card px-3 py-6">
            <View className="pl-3 mb-10">
                <BasisPragueLogo
                    style={{
                        width: 140,
                        height: 60,
                    }}
                    className="text-foreground"
                />
            </View>
            <View className="gap-1">
                {APP_NAV_ITEMS.map((item) => (
                    <View key={item.labelKey}>
                        {item.dividerBefore ? (
                            <Separator className="my-2" />
                        ) : null}
                        <SidebarNavItem
                            item={item}
                            isActive={isAppNavItemActive(pathname, item)}
                        />
                    </View>
                ))}
            </View>
            <View className="mt-auto gap-3">
                <ParentUserCard size="xs" />
                <Button
                    variant="outline"
                    onPress={() => signOut()}
                    testID={TEST_IDS[TEST_IDS.SIGN_OUT_BUTTON]}
                >
                    <Icon
                        name="right-from-bracket"
                        iconStyle="regular"
                        size={16}
                    />
                    <Text variant="label">{t("nav.signOut")}</Text>
                </Button>
            </View>
        </View>
    );
}
